import React from 'react';
import {Autocomplete, Box, Card, CardContent, Checkbox, FormControlLabel, Grid, Stack, TextField, Typography} from '@mui/material';
import {Person as PersonIcon} from '@mui/icons-material';
import {TaskWithImages} from '../hooks/useTaskDetail';
import type {Contact} from '../../../../../services/openapi/models/Contact';

interface TaskContactsProps {
    task: TaskWithImages | null;
    editMode: boolean;
    isTaskArchived: boolean;
    getCurrentValue: (field: string) => any;
    handleFieldUpdate: (field: string, value: any) => void;
    contacts: Contact[];
}

export const TaskContacts: React.FC<TaskContactsProps> = ({
    task,
    editMode,
    isTaskArchived,
    getCurrentValue,
    handleFieldUpdate,
    contacts,
}) => {
    const canEdit = editMode && !isTaskArchived;

    // Format contact as multi-line text for textarea
    const formatContactAsText = (contact: Contact): string => {
        const lines: string[] = [];
        if (contact.name) lines.push(contact.name);
        if (contact.email) lines.push(contact.email);
        if (contact.phone) lines.push(contact.phone);
        if (contact.address_text) lines.push(contact.address_text);
        return lines.join('\n');
    };

    // Handle contact selection for textarea mode
    const handleContactSelectTextarea = (prefix: string, contact: Contact | null) => {
        if (contact) {
            // For textarea mode
            handleFieldUpdate(`${prefix}_full_contact`, formatContactAsText(contact));
            // Also set individual fields
            handleFieldUpdate(`${prefix}_name`, contact.name || '');
            handleFieldUpdate(`${prefix}_email`, contact.email || '');
            handleFieldUpdate(`${prefix}_phone`, contact.phone || '');
        }
    };

    // Handle contact selection for structured mode (Einzelfelder)
    const handleContactSelectStructured = (prefix: string, nameFields: any, contact: Contact | null) => {
        if (contact) {
            handleFieldUpdate(nameFields.name, contact.name || '');
            handleFieldUpdate(nameFields.email, contact.email || '');
            handleFieldUpdate(nameFields.phone, contact.phone || '');
            // Parse address_text if available
            if (contact.address_text) {
                const parts = contact.address_text.split(',').map(p => p.trim());
                if (parts.length >= 2) {
                    handleFieldUpdate(nameFields.street, parts[0]);
                    const plzCityMatch = parts[1].match(/^(\d{5})\s*(.*)$/);
                    if (plzCityMatch) {
                        handleFieldUpdate(nameFields.postalCode, plzCityMatch[1]);
                        handleFieldUpdate(nameFields.city, plzCityMatch[2]);
                    } else {
                        handleFieldUpdate(nameFields.city, parts[1]);
                    }
                } else {
                    handleFieldUpdate(nameFields.street, contact.address_text);
                }
            }
        }
    };

    // Helper to format structured data for display
    const formatContactDisplay = (prefix: string) => {
        const name = getCurrentValue(`${prefix}_name`);
        const email = getCurrentValue(`${prefix}_email`);
        const phone = getCurrentValue(`${prefix}_phone`);
        const street = getCurrentValue(`${prefix}_street`);
        const additionalStreet = getCurrentValue(`${prefix}_additional_street`);
        const postalCode = getCurrentValue(`${prefix}_postal_code`);
        const city = getCurrentValue(`${prefix}_city`);
        const address = getCurrentValue(`${prefix}_address`); // Legacy field

        const parts = [
            name && `Name: ${name}`,
            email && `E-Mail: ${email}`,
            phone && `Tel: ${phone}`,
            address && `Adresse: ${address}`,
            street && `Straße: ${street}`,
            additionalStreet && `Zusatz: ${additionalStreet}`,
            (postalCode || city) && `${postalCode || ''} ${city || ''}`.trim()
        ].filter(Boolean);

        return parts.join('\n');
    };

    // Helper to check if structured fields have data
    const hasStructuredData = (prefix: string) => {
        return Boolean(
            getCurrentValue(`${prefix}_name`) ||
            getCurrentValue(`${prefix}_email`) ||
            getCurrentValue(`${prefix}_phone`) ||
            getCurrentValue(`${prefix}_street`) ||
            getCurrentValue(`${prefix}_city`) ||
            getCurrentValue(`${prefix}_address`)
        );
    };

    // Helper to format structured data to textarea content
    const formatStructuredToTextarea = (prefix: string) => {
        const name = getCurrentValue(`${prefix}_name`);
        const email = getCurrentValue(`${prefix}_email`);
        const phone = getCurrentValue(`${prefix}_phone`);
        const street = getCurrentValue(`${prefix}_street`);
        const additionalStreet = getCurrentValue(`${prefix}_additional_street`);
        const postalCode = getCurrentValue(`${prefix}_postal_code`);
        const city = getCurrentValue(`${prefix}_city`);

        const parts = [
            name,
            email,
            phone,
            street,
            additionalStreet,
            [postalCode, city].filter(Boolean).join(' ')
        ].filter(Boolean);

        return parts.join('\n');
    };

    // Determine if we should use textarea mode for each contact
    // For existing tasks: if use_full_contact is false, show structured mode
    // For existing tasks without the flag but with structured data: show structured mode
    const shouldUseTextarea = (prefix: string) => {
        const useFullContact = getCurrentValue(`${prefix}_use_full_contact`);
        if (useFullContact === false) return false;
        if (useFullContact === true) return true;
        // Legacy: if structured fields have data but no flag, show structured
        if (hasStructuredData(prefix)) return false;
        // Default to textarea for new entries
        return true;
    };

    // Handle mode toggle
    const handleModeToggle = (prefix: string, useStructured: boolean) => {
        if (!useStructured && !shouldUseTextarea(prefix)) {
            // Switching to textarea mode - auto-format existing data
            const formatted = formatStructuredToTextarea(prefix);
            handleFieldUpdate(`${prefix}_use_full_contact`, true);
            if (formatted) {
                handleFieldUpdate(`${prefix}_full_contact`, formatted);
            }
        } else {
            handleFieldUpdate(`${prefix}_use_full_contact`, !useStructured);
        }
    };

    // Render a contact section
    const renderContactSection = (
        title: string,
        prefix: string,
        nameFields: { name: string; email: string; phone: string; street: string; additionalStreet: string; postalCode: string; city: string; address?: string }
    ) => {
        const useTextarea = shouldUseTextarea(prefix);
        const fullContact = getCurrentValue(`${prefix}_full_contact`);
        const hasData = hasStructuredData(prefix) || Boolean(fullContact);

        return (
            <Grid item xs={12} md={4}>
                <Box sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    height: '100%',
                }}>
                    {canEdit ? (
                        <>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                                <Typography variant='subtitle1' fontWeight={600}>
                                    {title}
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size='small'
                                            checked={!useTextarea}
                                            onChange={(e) => handleModeToggle(prefix, e.target.checked)}
                                        />
                                    }
                                    label={<Typography variant='body2'>Einzelfelder</Typography>}
                                    sx={{mr: 0}}
                                />
                            </Box>
                            <Stack spacing={2}>
                                {useTextarea ? (
                                    <Box>
                                        <Autocomplete
                                            options={contacts}
                                            getOptionLabel={(option) => option.name}
                                            onChange={(_, newValue) => handleContactSelectTextarea(prefix, newValue)}
                                            size="small"
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Kontakt suchen"
                                                    placeholder="Kontakt auswählen..."
                                                    size="small"
                                                    sx={{mb: 1}}
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.id}>
                                                    <Box>
                                                        <Typography variant="body2">{option.name}</Typography>
                                                        {option.email && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {option.email}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </li>
                                            )}
                                        />
                                        <TextField
                                            fullWidth
                                            size='small'
                                            label='Kontaktdaten'
                                            value={fullContact || ''}
                                            onChange={(e) => handleFieldUpdate(`${prefix}_full_contact`, e.target.value)}
                                            variant='outlined'
                                            multiline
                                            rows={5}
                                            placeholder={'Name\nE-Mail\nTelefon\nAdresse'}
                                        />
                                    </Box>
                                ) : (
                                    <>
                                        <Autocomplete
                                            freeSolo
                                            options={contacts}
                                            getOptionLabel={(option) =>
                                                typeof option === 'string' ? option : option.name
                                            }
                                            value={contacts.find(c => c.name === getCurrentValue(nameFields.name)) || getCurrentValue(nameFields.name) || ''}
                                            onChange={(_, newValue) => {
                                                if (newValue === null) {
                                                    handleFieldUpdate(nameFields.name, '');
                                                } else if (typeof newValue === 'string') {
                                                    handleFieldUpdate(nameFields.name, newValue);
                                                } else {
                                                    handleContactSelectStructured(prefix, nameFields, newValue);
                                                }
                                            }}
                                            onInputChange={(_, newInputValue, reason) => {
                                                if (reason === 'input') {
                                                    handleFieldUpdate(nameFields.name, newInputValue);
                                                }
                                            }}
                                            size="small"
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Name"
                                                    placeholder="Kontakt auswählen oder eingeben..."
                                                    variant="outlined"
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.id}>
                                                    <Box>
                                                        <Typography variant="body2">{option.name}</Typography>
                                                        {option.email && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {option.email}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </li>
                                            )}
                                        />
                                        <TextField
                                            fullWidth
                                            size='small'
                                            label='E-Mail'
                                            type='email'
                                            value={getCurrentValue(nameFields.email) || ''}
                                            onChange={(e) => handleFieldUpdate(nameFields.email, e.target.value)}
                                            variant='outlined'
                                        />
                                        <TextField
                                            fullWidth
                                            size='small'
                                            label='Telefon'
                                            value={getCurrentValue(nameFields.phone) || ''}
                                            onChange={(e) => handleFieldUpdate(nameFields.phone, e.target.value)}
                                            variant='outlined'
                                        />
                                        {nameFields.address && getCurrentValue(nameFields.address) ? (
                                            <TextField
                                                fullWidth
                                                size='small'
                                                label='Adresse'
                                                value={getCurrentValue(nameFields.address) || ''}
                                                onChange={(e) => handleFieldUpdate(nameFields.address!, e.target.value)}
                                                variant='outlined'
                                                multiline
                                                rows={2}
                                            />
                                        ) : (
                                            <>
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    label='Straße & Hausnr.'
                                                    value={getCurrentValue(nameFields.street) || ''}
                                                    onChange={(e) => handleFieldUpdate(nameFields.street, e.target.value)}
                                                    variant='outlined'
                                                />
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    label='Adresszusatz'
                                                    value={getCurrentValue(nameFields.additionalStreet) || ''}
                                                    onChange={(e) => handleFieldUpdate(nameFields.additionalStreet, e.target.value)}
                                                    variant='outlined'
                                                />
                                                <Box sx={{display: 'flex', gap: 2}}>
                                                    <TextField
                                                        size='small'
                                                        label='PLZ'
                                                        value={getCurrentValue(nameFields.postalCode) || ''}
                                                        onChange={(e) => handleFieldUpdate(nameFields.postalCode, e.target.value)}
                                                        variant='outlined'
                                                        sx={{width: '30%'}}
                                                    />
                                                    <TextField
                                                        size='small'
                                                        label='Stadt'
                                                        value={getCurrentValue(nameFields.city) || ''}
                                                        onChange={(e) => handleFieldUpdate(nameFields.city, e.target.value)}
                                                        variant='outlined'
                                                        sx={{flex: 1}}
                                                    />
                                                </Box>
                                            </>
                                        )}
                                    </>
                                )}
                            </Stack>
                        </>
                    ) : (
                        // Display mode (read-only)
                        <>
                            <Typography variant='subtitle1' fontWeight={600} sx={{mb: 2}}>
                                {title}
                            </Typography>
                            {hasData ? (
                                useTextarea && fullContact ? (
                                    <Typography variant='body1' color='text.primary' sx={{lineHeight: 1.8, whiteSpace: 'pre-line'}}>
                                        {fullContact}
                                    </Typography>
                                ) : (
                                    <Typography variant='body1' color='text.primary' sx={{lineHeight: 1.8, whiteSpace: 'pre-line'}}>
                                        {formatContactDisplay(prefix)}
                                    </Typography>
                                )
                            ) : (
                                <Typography variant='body2' color='text.disabled' sx={{fontStyle: 'italic'}}>
                                    Keine {title}-Informationen hinterlegt
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </Grid>
        );
    };

    return (
        <Card variant='outlined' sx={{mb: 3}}>
            <CardContent>
                <Box display='flex' alignItems='center' gap={1} mb={3}>
                    <PersonIcon color='primary'/>
                    <Typography variant='h6'>Kontaktdaten</Typography>
                </Box>
                <Grid container spacing={3}>
                    {renderContactSection(
                        'Auftraggeber Kontaktdaten',
                        'auftraggeber',
                        {
                            name: 'auftraggeber_name',
                            email: 'auftraggeber_email',
                            phone: 'auftraggeber_phone',
                            street: 'auftraggeber_street',
                            additionalStreet: 'auftraggeber_additional_street',
                            postalCode: 'auftraggeber_postal_code',
                            city: 'auftraggeber_city',
                            address: 'auftraggeber_address'
                        }
                    )}
                    {renderContactSection(
                        'Terminvereinbarung',
                        'appointment',
                        {
                            name: 'appointment_name',
                            email: 'appointment_email',
                            phone: 'appointment_phone',
                            street: 'appointment_street',
                            additionalStreet: 'appointment_additional_street',
                            postalCode: 'appointment_postal_code',
                            city: 'appointment_city'
                        }
                    )}
                    {renderContactSection(
                        'Rechnungsempfänger',
                        'invoice',
                        {
                            name: 'invoice_name',
                            email: 'invoice_email',
                            phone: 'invoice_phone',
                            street: 'invoice_street',
                            additionalStreet: 'invoice_additional_street',
                            postalCode: 'invoice_postal_code',
                            city: 'invoice_city'
                        }
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
};
